// ─── Asset Control — TypeScript interfaces ──────────────────────────────────

export enum AssetLocationGroup {
  BODEGA = 'BODEGA',
  SALA = 'SALA',
  GERENCIA = 'GERENCIA',
  OFICINA = 'OFICINA',
  ZONA_COMUN = 'ZONA_COMUN',
  ESTACION = 'ESTACION',
  COMEDOR = 'COMEDOR',
  DEPARTAMENTO = 'DEPARTAMENTO',
  /** Off-site: the asset is out at a repair shop. */
  MANTENIMIENTO = 'MANTENIMIENTO',
}

export enum AssetHistoryAction {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
  DISPOSE = 'DISPOSE',
  RESTORE = 'RESTORE',
}

/** Why an asset was written off the inventory. */
export enum AssetDisposalReason {
  NACIONALIZADO = 'NACIONALIZADO',
  DANADO = 'DANADO',
  OBSOLETO = 'OBSOLETO',
  VENDIDO = 'VENDIDO',
  PERDIDO = 'PERDIDO',
  OTRO = 'OTRO',
}

// Catalog keys: built at import time, resolved where the label is rendered.
export const ASSET_DISPOSAL_REASON_LABEL: Record<AssetDisposalReason, string> = {
  [AssetDisposalReason.NACIONALIZADO]: 'assets.disposal.reason.nationalised',
  [AssetDisposalReason.DANADO]: 'assets.disposal.reason.damaged',
  [AssetDisposalReason.OBSOLETO]: 'assets.disposal.reason.obsolete',
  [AssetDisposalReason.VENDIDO]: 'assets.disposal.reason.sold',
  [AssetDisposalReason.PERDIDO]: 'assets.disposal.reason.lost',
  [AssetDisposalReason.OTRO]: 'assets.disposal.reason.other',
};

/** Which side of the inventory a listing looks at. */
export enum AssetStatusFilter {
  ACTIVE = 'ACTIVE',
  DISPOSED = 'DISPOSED',
  ALL = 'ALL',
}

/** Where the asset is kept: an office location or the assignee's home. */
export enum AssetCustody {
  OFICINA = 'OFICINA',
  CASA_COLABORADOR = 'CASA_COLABORADOR',
}

// Catalog keys: built at import time, resolved by `custodyLabel()`.
export const ASSET_CUSTODY_LABEL: Record<AssetCustody, string> = {
  [AssetCustody.OFICINA]: 'custody.office',
  [AssetCustody.CASA_COLABORADOR]: 'custody.employeeHome',
};

/** Asset age buckets for the "Antigüedad" filter (computed from purchaseDate). */
export enum AssetAntiquityRange {
  TODOS = 'TODOS',
  LESS_THAN_1 = 'LESS_THAN_1',
  ONE_TO_THREE = 'ONE_TO_THREE',
  THREE_TO_FIVE = 'THREE_TO_FIVE',
  FIVE_TO_TEN = 'FIVE_TO_TEN',
  TEN_PLUS = 'TEN_PLUS',
}

export enum AppRole {
  SUPER_ADMIN = 'SUPER_ADMIN',
  DESKBOOKING_ADMIN = 'DESKBOOKING_ADMIN',
  LUNCH_ADMIN = 'LUNCH_ADMIN',
  ASSET_ADMIN = 'ASSET_ADMIN',
  ASSET_VIEWER = 'ASSET_VIEWER',
  REPORTS_VIEWER = 'REPORTS_VIEWER',
  SKILLS_ADMIN = 'SKILLS_ADMIN',
  SKILLS_VIEWER = 'SKILLS_VIEWER',
}

// i18n-exempt-start — these are `asset_category.name` values, used to filter
// the category list. They have to match the database exactly.
export const ALLOWED_ASSET_CATEGORIES: string[] = [
  'Mobiliario Oficina',
  'Equipo IT',
  'Mejoras',
];
// i18n-exempt-end

export interface IAssetCategory {
  id: number;
  name: string;
  /** English name, absent when there is none. Read it via `localizedName()`. */
  nameEn?: string | null;
  isActive: boolean;
  subcategories?: IAssetSubcategory[];
}

export interface IAssetSubcategory {
  id: number;
  name: string;
  categoryId: number;
  isActive: boolean;
}

export interface IAssetLocation {
  id: number;
  name: string;
  /** English name, absent when there is none. Read it via `localizedName()`. */
  nameEn?: string | null;
  group: AssetLocationGroup;
  isActive: boolean;
  sortOrder: number;
}

export interface IAssetLocationWithCount extends IAssetLocation {
  assetCount: number;
}

/** Deskbooking user selectable as the holder of an asset. */
export interface IAssetAssignee {
  email: string;
  name: string;
  isActive: boolean;
  blocked: number;
}

export interface IAsset {
  id: number;
  plateNumber: string;
  serialNumber: string | null;
  brand: string | null;
  model: string | null;
  categoryId: number | null;
  subcategoryId: number | null;
  locationId: number | null;
  custody: AssetCustody;
  purchaseDate: string | null;
  paysVat: boolean;
  assignedToEmail: string | null;
  assignedTo?: IAssetAssignee | null;
  photoUrl: string | null;
  createdByEmail: string | null;
  updatedByEmail: string | null;
  createdAt: string;
  updatedAt: string;
  /** Set once the asset is written off. null means it is still in circulation. */
  disposedAt: string | null;
  disposedByEmail: string | null;
  disposalReason: AssetDisposalReason | null;
  disposalNote: string | null;
  category?: IAssetCategory | null;
  subcategory?: IAssetSubcategory | null;
  location?: IAssetLocation | null;
}

export interface IAssetPage {
  items: IAsset[];
  total: number;
  page: number;
  pageSize: number;
}

export interface IAssetSearchSuggestion {
  id: number;
  plateNumber: string;
  brand: string | null;
  model: string | null;
  custody: AssetCustody | null;
  categoryName: string | null;
  locationName: string | null;
  assignedToName: string | null;
}

export interface IAssetHistory {
  id: number;
  assetId: number;
  action: AssetHistoryAction;
  field: string | null;
  oldValue: string | null;
  newValue: string | null;
  changedByEmail: string;
  changedAt: string;
}

export interface IUserRole {
  id: number;
  userEmail: string;
  role: AppRole;
  createdAt: string;
  createdByEmail: string | null;
}

// ─── Inventory audit ────────────────────────────────────────────────────────

/** What one barcode scan in an audit turned out to be. */
export enum AssetAuditScanResult {
  FOUND = 'FOUND',
  MISPLACED = 'MISPLACED',
  UNKNOWN = 'UNKNOWN',
  DISPOSED = 'DISPOSED',
  DUPLICATE = 'DUPLICATE',
}

// Catalog keys: built at import time, resolved where the badge is rendered.
export const ASSET_AUDIT_RESULT_LABEL: Record<AssetAuditScanResult, string> = {
  [AssetAuditScanResult.FOUND]: 'assets.audit.result.found',
  [AssetAuditScanResult.MISPLACED]: 'assets.audit.result.misplaced',
  [AssetAuditScanResult.UNKNOWN]: 'assets.audit.result.unknown',
  [AssetAuditScanResult.DISPOSED]: 'assets.audit.result.disposed',
  [AssetAuditScanResult.DUPLICATE]: 'assets.audit.result.duplicate',
};

export interface IAssetAuditCounts {
  scanned: number;
  found: number;
  misplaced: number;
  unknown: number;
  disposed: number;
  duplicate: number;
  expected: number;
  missing: number;
}

export interface IAssetAudit {
  id: number;
  /** null means the whole office. */
  locationId: number | null;
  location: IAssetLocation | null;
  note: string | null;
  startedByEmail: string;
  startedAt: string;
  closedAt: string | null;
  closedByEmail: string | null;
  counts: IAssetAuditCounts;
}

/** The scanned asset as it is now (it may have moved since the scan). */
export type IAssetAuditAsset = Pick<
  IAsset,
  'id' | 'plateNumber' | 'brand' | 'model' | 'locationId' | 'custody' | 'disposedAt'
> & {
  category?: Pick<IAssetCategory, 'id' | 'name' | 'nameEn'> | null;
  location?: IAssetLocation | null;
};

export interface IAssetAuditScan {
  id: number;
  auditId: number;
  code: string;
  assetId: number | null;
  result: AssetAuditScanResult;
  /** Where the record placed the asset when it was scanned. */
  assetLocationIdAtScan: number | null;
  locationAtScan: IAssetLocation | null;
  scannedByEmail: string;
  scannedAt: string;
  asset: IAssetAuditAsset | null;
}

export interface IAssetAuditMissingItem {
  asset: IAssetAuditAsset;
  expectedLocation: IAssetLocation | null;
}

export interface IAssetAuditReport {
  audit: IAssetAudit;
  scans: IAssetAuditScan[];
  missing: IAssetAuditMissingItem[];
}

// ─── Inputs ─────────────────────────────────────────────────────────────────

export interface ICreateAssetInput {
  plateNumber: string;
  serialNumber?: string | null;
  brand?: string | null;
  model?: string | null;
  categoryId?: number | null;
  subcategoryId?: number | null;
  locationId?: number | null;
  purchaseDate?: string | null;
  paysVat: boolean;
}

export interface IUpdateAssetInput extends Partial<ICreateAssetInput> {
  id: number;
}

export interface IAssetSearchArgs {
  search?: string;
  categoryId?: number;
  subcategoryId?: number;
  locationId?: number;
  antiquity?: AssetAntiquityRange;
  page?: number;
  pageSize?: number;
}
